import { z } from 'zod';

const schema = z.object({
  VITE_APP_ENV: z
    .enum(['local', 'test', 'staging', 'training', 'production'])
    .default('local'),
  VITE_API_BASE_URL: z
    .string()
    .default('/api/v1')
    .refine((v) => v.startsWith('/') || /^https?:\/\//.test(v), {
      message: 'must be a path like /api/v1 or an absolute http(s) URL',
    })
    .transform((v) => v.replace(/\/+$/, '')),
  VITE_API_TIMEOUT_MS: z.coerce
    .number()
    .int()
    .min(1000)
    .max(120000)
    .default(15000),
  /**
   * Where "Open Ommri Care app" points after a web password reset (an https
   * universal link, set once the apps handle one). Unset: only text is shown.
   */
  VITE_PATIENT_APP_URL: z
    .string()
    .trim()
    .optional()
    .transform((v) => (v ? v : undefined))
    .refine((v) => v === undefined || /^https:\/\/[^\s]+$/.test(v), {
      message: 'must be an https URL',
    }),
});

export type AppEnv = {
  appEnv: 'local' | 'test' | 'staging' | 'training' | 'production';
  apiBaseUrl: string;
  apiTimeoutMs: number;
  patientAppUrl: string | undefined;
};

export function parseEnv(raw: Record<string, unknown>): AppEnv {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const issues = result.error.issues
      .map((i) => `${i.path.join('.')}: ${i.message}`)
      .join('; ');
    throw new Error(`Invalid admin configuration: ${issues}`);
  }
  const e = result.data;
  return {
    appEnv: e.VITE_APP_ENV,
    apiBaseUrl: e.VITE_API_BASE_URL,
    apiTimeoutMs: e.VITE_API_TIMEOUT_MS,
    patientAppUrl: e.VITE_PATIENT_APP_URL,
  };
}

/** Validated build-time configuration. Throws at startup if misconfigured. */
export const env: AppEnv = parseEnv(import.meta.env);
